import React, { useEffect, useRef } from 'react';

const IOSAudioPlayer = ({ 
  audioUrl, 
  onPlaying, 
  onLoading, 
  onFinishedPlaying, 
  onError, 
  volume, 
  playbackRate,
  soundKey,
  unlockedAudio,  // pre-unlocked Audio element created during a user gesture
}) => {
  const audioRef = useRef(null);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    // Re-use the pre-unlocked element if provided; otherwise create a new one.
    // The pre-unlocked element has already had .play()/.pause() called inside a
    // user-gesture handler, so iOS Safari will allow future .play() calls on it.
    const audio = unlockedAudio || new Audio();
    audioRef.current = audio;

    // Reset to the new source (important when re-using the unlocked element)
    audio.pause();
    audio.currentTime = 0;
    audio.src = audioUrl;
    audio.preload = 'auto';
    audio.volume = volume / 100;
    audio.playbackRate = playbackRate;
    
    // Event listeners
    const handleCanPlayThrough = () => {
      if (onLoading) {
        onLoading({ duration: audio.duration * 1000 });
      }
      
      // Try to play
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('iOS audio started playing');
            
            // Start progress updates
            const updateProgress = () => {
              const now = Date.now();
              if (now - lastUpdateRef.current > 100) { // Update every 100ms
                if (onPlaying && audio.duration) {
                  onPlaying({
                    position: audio.currentTime * 1000,
                    duration: audio.duration * 1000
                  });
                }
                lastUpdateRef.current = now;
              }
              animationRef.current = requestAnimationFrame(updateProgress);
            };
            
            animationRef.current = requestAnimationFrame(updateProgress);
          })
          .catch(err => {
            console.error('iOS audio play failed:', err);
            if (onError) {
              onError('PLAYBACK_ERROR', err.message);
            }
          });
      }
    };
    
    const handleEnded = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (onFinishedPlaying) {
        onFinishedPlaying();
      }
    };
    
    const handleError = (e) => {
      console.error('iOS audio error:', e);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (onError) {
        onError('LOAD_ERROR', e.message);
      }
    };
    
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    
    audio.load();
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('canplaythrough', handleCanPlayThrough);
        audioRef.current.removeEventListener('ended', handleEnded);
        audioRef.current.removeEventListener('error', handleError);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioUrl, volume, playbackRate, soundKey]);

  // Update volume and playback rate when props change
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  return null; // This component doesn't render anything
};

export default IOSAudioPlayer;